export abstract class ViewModelBase {
  private originalValues?: Map<string, any>;

  get hasChanges(): boolean {
    return (this.originalValues?.size ?? 0) > 0;
  }

  constructor(private item?: any) {}

  protected super(item: any) {
    if (item) {
      this.item = item;
    }
  }

  protected isChanged(property: string): boolean {
    return this.originalValues !== undefined && this.originalValues.has(property);
  }

  protected getOriginalValue(property: string): any {
    return this.isChanged(property) ? this.originalValues!.get(property) : this.getValueCore(this.item, property);
  }

  protected setValue(key: string, oldValue: any, newValue: any, item?: any): boolean {
    if (oldValue === newValue) {
      return false;
    }

    this.setValueCore(item ?? this.item, key, newValue);

    if (!this.originalValues) {
      this.originalValues = new Map<string, any>();
    }

    if (!this.originalValues.has(key)) {
      this.originalValues.set(key, oldValue);
    } else if (this.originalValues.get(key) === newValue) {
      this.originalValues.delete(key);
    }

    return true;
  }

  protected setValueCore(item: any, key: string, value: any): void {
    if (key.indexOf('.') >= 0) {
      const keys = key.split('.');

      for (let i = 0; i < keys.length - 1; i++) {
        item = item[keys[i]];
      }

      item[keys[keys.length - 1]] = value;
    } else {
      item[key] = value;
    }
  }

  private getValueCore(item: any, key: string): any {
    if (key.indexOf('.') >= 0) {
      const keys = key.split('.');

      for (let i = 0; i < keys.length - 1; i++) {
        item = item[keys[i]];
      }

      return item[keys[keys.length - 1]];
    } else {
      return item[key];
    }
  }

  reset(): void {
    if (this.originalValues) {
      this.originalValues.forEach((value, key) => {
        this.setValueCore(this.item, key, value);
      });
      this.originalValues = undefined;
    }
  }

  accept(): void {
    this.originalValues = undefined;
  }
}

export class ClickableData {
  id: string;
  objectType: number;
  left: number;
  right: number;
  top: number;
  bottom: number;
  points?: [{ x: number; y: number }];

  constructor(data?: any) {
    Object.assign(this, data);
  }
}

export class LongitudinalSectionPipe {
  id: string;
  objectType: string | number;
  isInFlowDirection: boolean;
}

export class LongitudinalSectionState {
  verticalScale: number | null = null;
  horizontalScale: number | null = null;

  reportFilename: string = '';

  imageBase64: string;
  imageWidth: number;
  imageHeight: number;
  clickableData: ClickableData[];
}

export class LongitudinalSectionSettings {
  graphicsDocumentId: string = '';
  graphicsDocuments: { id: string; displayText: string }[] = [];
  reportDocumentId: string = '';
  reportDocuments: { id: string; displayText: string }[] = [];
  verticalScale: number | null = null;
  verticalScales: { id: number; displayText: string }[] = [];
  horizontalScale: number | null = null;
  horizontalScales: { id: number; displayText: string }[] = [];

  constructor(data?: any) {
    Object.assign(this, data);
  }
}

export class LongitudinalSectionSettingsViewModel extends ViewModelBase {
  get graphicsDocumentId(): string {
    return this.longitudinalSectionSettings.graphicsDocumentId ?? '';
  }
  set graphicsDocumentId(value: string) {
    this.setValue('graphicsDocumentId', this.graphicsDocumentId, value ?? '');
  }
  get graphicsDocumentIdHasChanged(): boolean {
    return this.isChanged('graphicsDocumentId');
  }

  get reportDocumentId(): string {
    return this.longitudinalSectionSettings.reportDocumentId ?? '';
  }
  set reportDocumentId(value: string) {
    this.setValue('reportDocumentId', this.reportDocumentId, value ?? '');
  }
  get reportDocumentIdHasChanged(): boolean {
    return this.isChanged('reportDocumentId');
  }

  get verticalScale(): number | null {
    return this.longitudinalSectionSettings.verticalScale ?? null;
  }
  set verticalScale(value: number | null) {
    this.setValue('verticalScale', this.verticalScale, value ?? null);
  }
  get verticalScaleHasChanged(): boolean {
    return this.isChanged('verticalScale');
  }

  get horizontalScale(): number | null {
    return this.longitudinalSectionSettings.horizontalScale ?? null;
  }
  set horizontalScale(value: number | null) {
    this.setValue('horizontalScale', this.horizontalScale, value ?? null);
  }
  get horizontalScaleHasChanged(): boolean {
    return this.isChanged('horizontalScale');
  }

  constructor(readonly longitudinalSectionSettings: LongitudinalSectionSettings) {
    super(longitudinalSectionSettings);
  }
}
